import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { MapPin, Globe, Phone, Mail, GraduationCap, School, Building2, Calendar, ShieldCheck, BadgeCheck, X, BookOpen, Image as ImageIcon, Award, Newspaper, Package, Wrench, Star } from "lucide-react";
import { toast } from "sonner";
import MagazineProfile, { hasMagazine } from "@/components/MagazineProfile";
import { AddMyProfileCTA, Pagination } from "@/components/DirectoryHelpers";

const PAGE_SIZE = 9;

const ICONS = { school: School, college: GraduationCap, university: GraduationCap };

/**
 * Colored section head — round pastel icon + editorial title.
 * color is one of: sky, emerald, amber, rose, violet, slate
 */
function SectionHead({ icon: Icon, color = "sky", title, small = false }) {
  const map = {
    sky: "bg-sky-100 text-sky-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
    violet: "bg-violet-100 text-violet-700",
    slate: "bg-slate-200 text-slate-700",
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`grid place-items-center rounded-full ${map[color] || map.sky} ${small ? "w-8 h-8" : "w-10 h-10"}`}>
        <Icon className={small ? "w-4 h-4" : "w-5 h-5"}/>
      </div>
      <h2 className={`font-editorial font-bold text-slate-900 ${small ? "text-lg" : "text-2xl"}`}>{title}</h2>
    </div>
  );
}

export function DirectoryPage({ type, title, description, testId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [search, type]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
    if (type) params.type = type;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/institutions", { params }).then(r => {
        setItems(r.data.items);
        setTotal(r.data.total || 0);
      }).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [type, search, page]);

  const Icon = ICONS[type] || GraduationCap;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="overline text-sky-700 mb-2">Directory</div>
          <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-3">{title}</h1>
          <p className="text-slate-600 max-w-2xl mb-6">{description}</p>
        </div>
        <AddMyProfileCTA testId={`${testId}-cta`}/>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${type || "institutions"}...`}
        className="w-full max-w-md px-5 py-3 rounded-full bg-slate-100 border border-transparent focus:border-sky-400 focus:bg-white outline-none text-sm mb-8"
        data-testid={`${testId}-search`} />

      {loading ? <div className="text-slate-500">Loading…</div> : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(i => (
              <Link key={i.id} to={`/institution/${i.slug}`} className="card-soft overflow-hidden hover-zoom relative" data-testid={`institution-card-${i.id}`}>
                {i.featured && (
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "#F0C948", color: "#0F172A" }}>
                    <Star className="w-3 h-3 fill-current"/> Featured
                  </span>
                )}
                {i.banner && <div className="aspect-[16/9] overflow-hidden bg-slate-100"><img src={i.banner} alt={i.name} className="w-full h-full object-cover"/></div>}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 grid place-items-center"><Icon className="w-4 h-4"/></div>
                    <span className="overline text-[10px] text-slate-500">{i.type}</span>
                  </div>
                  <h3 className="font-editorial text-lg font-bold text-slate-900 leading-snug title-link">{i.name}</h3>
                  <div className="mt-3 flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3 h-3"/>{i.city}, {i.state}</div>
                </div>
              </Link>
            ))}
            {!items.length && <div className="text-slate-500 col-span-full">No matches.</div>}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => Math.min(totalPages, p + 1))}
            testId={`${testId}-pagination`}
          />
        </>
      )}
    </div>
  );
}

export function SchoolsPage() { return <DirectoryPage type="school" title="Explore Schools" description="Discover top K-12 schools across India — profiles, admissions, achievements and more." testId="schools-page" />; }
export function CollegesPage() { return <DirectoryPage type="college" title="Colleges & Universities" description="Complete directory of colleges and universities. Browse programs, placements, and rankings." testId="colleges-page" />; }

export function SuppliersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => {
    const t = setTimeout(() =>
      api.get("/suppliers", { params: { search, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE } })
        .then(r => { setItems(r.data.items); setTotal(r.data.total || 0); }),
    250);
    return () => clearTimeout(t);
  }, [search, page]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="suppliers-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="overline text-sky-700 mb-2">Directory</div>
          <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-3">Education Suppliers</h1>
          <p className="text-slate-600 max-w-2xl mb-6">Trusted vendors and partners — ERP, EdTech, furniture, publishers, and smart classroom providers.</p>
        </div>
        <AddMyProfileCTA testId="suppliers-cta"/>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search suppliers..." className="w-full max-w-md px-5 py-3 rounded-full bg-slate-100 focus:bg-white outline-none text-sm mb-8" data-testid="suppliers-search"/>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(s => (
          <Link key={s.id} to={`/supplier/${s.slug}`} className="card-soft p-6 block hover-zoom relative" data-testid={`supplier-card-${s.id}`}>
            {s.featured && (
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "#F0C948", color: "#0F172A" }}>
                <Star className="w-3 h-3 fill-current"/> Featured
              </span>
            )}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Building2 className="w-4 h-4"/></div>
              <span className="overline text-[10px] text-emerald-700 px-2.5 py-1 rounded-full tag-green">{s.category}</span>
            </div>
            <h3 className="font-editorial text-lg font-bold text-slate-900 title-link">{s.name}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.description}</p>
            <div className="mt-3 flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3 h-3"/>{s.city}, {s.state}</div>
          </Link>
        ))}
        {!items.length && <div className="text-slate-500 col-span-full">No matches.</div>}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage(p => Math.max(1, p - 1))}
        onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        testId="suppliers-pagination"
      />
    </div>
  );
}

export function InstitutionPage() {
  const { slug } = useParams();
  const [i, setI] = useState(null);
  const user = getUser();
  const isAdmin = user?.role === "admin" || user?.role === "editor";
  const reload = () => api.get(`/institutions/${slug}`).then(r => setI(r.data)).catch(() => setI(null));
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);
  if (!i) return <div className="max-w-4xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;

  const handleSave = async (fields) => {
    // Preserve required + existing fields, patch magazine + masthead
    const payload = {
      ...i,
      name: fields.masthead || i.name,
      top_photos: fields.top_photos,
      col_left_html: fields.col_left_html,
      col_right_html: fields.col_right_html,
      masthead_year: fields.year,
    };
    await api.put(`/institutions/${i.id}`, payload);
    await reload();
  };

  // New magazine layout when the profile has been converted (top_photos + col_left_html / col_right_html)
  // — OR when the current user is an admin (so they can add magazine content on a fresh profile).
  if (hasMagazine(i) || isAdmin) {
    return (
      <MagazineProfile
        photos={i.top_photos || []}
        leftHtml={i.col_left_html}
        rightHtml={i.col_right_html}
        masthead={i.name}
        year={i.masthead_year || new Date().getFullYear()}
        filename={i.name}
        canEdit={isAdmin}
        onSave={handleSave}
        testId="institution-magazine"
      />
    );
  }

  return (
    <div data-testid="institution-page" className="bg-gradient-to-b from-sky-50/40 via-white to-white">
      {/* Header block with pastel gradient */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-6">
        <div className="rounded-[28px] bg-gradient-to-br from-sky-50 via-white to-emerald-50/60 border border-sky-100/80 p-6 md:p-10 shadow-sm">
          <div className="flex items-start justify-between gap-6 flex-wrap md:flex-nowrap">
            <div className="min-w-0 flex-1">
              <div className="overline mb-2 text-sky-700">{i.type} · Est. {i.established_year}</div>
              <h1 className="headline text-3xl md:text-5xl font-bold text-slate-900 flex flex-wrap items-center gap-3" data-testid="institution-name">
                {i.name}
                {i.verified && <span className="inline-flex items-center gap-1 text-xs bg-emerald-500 text-white px-2 py-1 rounded-full font-semibold" title="Verified institution"><BadgeCheck className="w-4 h-4"/>Verified</span>}
              </h1>
              <div className="text-sm mt-3 text-slate-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-700"/>{i.city}, {i.state}</div>
              {i.affiliation && <div className="text-sm mt-2 text-slate-500"><span className="font-semibold text-slate-700">Affiliation:</span> {i.affiliation}</div>}
            </div>
            {i.logo && (
              <div className="shrink-0" data-testid="institution-logo">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-white border border-slate-200 shadow-md p-3 flex items-center justify-center overflow-hidden">
                  <img src={i.logo} alt={`${i.name} logo`} className="max-w-full max-h-full object-contain"/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {i.gallery?.length > 0 && (
            <section className="rounded-3xl bg-violet-50/50 border border-violet-100 p-6 md:p-8" data-testid="institution-gallery">
              <SectionHead icon={ImageIcon} color="violet" title="Photo Gallery"/>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">{i.gallery.map((g, idx) => <div key={idx} className="aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-sm ring-1 ring-violet-100"><img src={g} alt="" className="w-full h-full object-cover"/></div>)}</div>
            </section>
          )}
          <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
            <SectionHead icon={BookOpen} color="sky" title="About"/>
            <div className="mt-5">
              {i.description_html
                ? <div className="prose-editorial text-slate-800" dangerouslySetInnerHTML={{ __html: (i.description_html || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }} data-testid="institution-description-html"/>
                : <p className="text-slate-700 leading-relaxed">{i.description}</p>}
            </div>
          </section>
          {i.courses?.length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={GraduationCap} color="emerald" title="Courses & Programs"/>
              <div className="flex flex-wrap gap-2 mt-5">{i.courses.map(c => <span key={c} className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-800 text-xs font-semibold shadow-sm">{c}</span>)}</div>
            </section>
          )}
          {i.facilities?.length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Building2} color="amber" title="Facilities"/>
              <div className="flex flex-wrap gap-2 mt-5">{i.facilities.map(f => <span key={f} className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-amber-800 text-xs font-semibold shadow-sm">{f}</span>)}</div>
            </section>
          )}
          {i.news?.length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Newspaper} color="rose" title="Recent News"/>
              <div className="space-y-3 mt-5">
                {i.news.map(n => (
                  <Link key={n.id || n.slug} to={`/article/${n.slug}`} className="bg-white rounded-2xl p-4 flex items-center gap-4 hover-zoom border border-slate-100 shadow-sm">
                    {n.featured_image && <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-slate-100"><img src={n.featured_image} alt="" className="w-full h-full object-cover"/></div>}
                    <div className="font-editorial font-semibold text-slate-900 leading-snug title-link">{n.title}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
        <aside className="space-y-5">
          <div className="rounded-3xl p-6 space-y-3 text-sm" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
            <SectionHead icon={Phone} color="amber" title="Contact" small/>
            <div className="space-y-2.5 pt-2">
              {i.principal && <div className="text-slate-700"><span className="font-semibold text-slate-900">Principal:</span> {i.principal}</div>}
              {i.website && <div className="flex items-center gap-2 text-sky-700"><Globe className="w-4 h-4"/><a href={i.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{i.website}</a></div>}
              {i.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-sky-700"/><a href={`mailto:${i.email}`} className="hover:underline break-all">{i.email}</a></div>}
              {i.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-sky-700"/><a href={`tel:${i.phone}`} className="hover:underline">{i.phone}</a></div>}
              {i.address && <div className="flex items-start gap-2 text-slate-700"><MapPin className="w-4 h-4 mt-0.5 text-sky-700"/>{i.address}</div>}
            </div>
          </div>
          {i.achievements?.length > 0 && (
            <div className="rounded-3xl p-6" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
              <SectionHead icon={Award} color="amber" title="Achievements" small/>
              <ul className="space-y-2 text-sm text-slate-700 mt-3">{i.achievements.map(a => <li key={a} className="flex gap-2"><Calendar className="w-3.5 h-3.5 mt-1 text-amber-700 shrink-0"/>{a}</li>)}</ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export function SupplierPage() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const user = getUser();
  const isAdmin = user?.role === "admin" || user?.role === "editor";
  const reload = () => api.get(`/suppliers/${slug}`).then(r => setS(r.data)).catch(() => setS(null));
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);
  if (!s) return <div className="max-w-4xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;

  const handleSave = async (fields) => {
    const payload = {
      ...s,
      name: fields.masthead || s.name,
      top_photos: fields.top_photos,
      col_left_html: fields.col_left_html,
      col_right_html: fields.col_right_html,
      masthead_year: fields.year,
    };
    await api.put(`/suppliers/${s.id}`, payload);
    await reload();
  };

  if (hasMagazine(s) || isAdmin) {
    return (
      <MagazineProfile
        photos={s.top_photos || []}
        leftHtml={s.col_left_html}
        rightHtml={s.col_right_html}
        masthead={s.name}
        year={s.masthead_year || new Date().getFullYear()}
        filename={s.name}
        canEdit={isAdmin}
        onSave={handleSave}
        testId="supplier-magazine"
      />
    );
  }

  return (
    <div data-testid="supplier-page" className="bg-gradient-to-b from-emerald-50/40 via-white to-white">
      {/* Header block */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-6">
        <div className="rounded-[28px] bg-gradient-to-br from-emerald-50 via-white to-sky-50/60 border border-emerald-100/80 p-6 md:p-10 shadow-sm">
          <div className="flex items-start justify-between gap-6 flex-wrap md:flex-nowrap">
            <div className="min-w-0 flex-1">
              <div className="overline text-emerald-700 mb-2">{s.category}</div>
              <h1 className="headline text-3xl md:text-5xl font-bold text-slate-900 mb-3">{s.name}</h1>
              <p className="text-slate-600 max-w-2xl">{s.description}</p>
            </div>
            {s.logo && (
              <div className="shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-white border border-slate-200 shadow-md p-3 flex items-center justify-center overflow-hidden">
                  <img src={s.logo} alt={`${s.name} logo`} className="max-w-full max-h-full object-contain"/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {s.gallery?.length > 0 && (
            <section className="rounded-3xl bg-violet-50/50 border border-violet-100 p-6 md:p-8" data-testid="supplier-gallery">
              <SectionHead icon={ImageIcon} color="violet" title="Photo Gallery"/>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">{s.gallery.map((g, idx) => <div key={idx} className="aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-sm ring-1 ring-violet-100"><img src={g} alt="" className="w-full h-full object-cover"/></div>)}</div>
            </section>
          )}
          {s.description_html && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }} data-testid="supplier-description-html">
              <SectionHead icon={BookOpen} color="sky" title="About"/>
              <div className="prose-editorial text-slate-800 mt-5" dangerouslySetInnerHTML={{ __html: (s.description_html || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }}/>
            </section>
          )}
          {(s.products?.length > 0 || s.services?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              {s.products?.length > 0 && (
                <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
                  <SectionHead icon={Package} color="emerald" title="Products" small/>
                  <ul className="mt-4 space-y-2 text-slate-800 text-sm">{s.products.map(p => <li key={p} className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"/>{p}</li>)}</ul>
                </section>
              )}
              {s.services?.length > 0 && (
                <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
                  <SectionHead icon={Wrench} color="amber" title="Services" small/>
                  <ul className="mt-4 space-y-2 text-slate-800 text-sm">{s.services.map(p => <li key={p} className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"/>{p}</li>)}</ul>
                </section>
              )}
            </div>
          )}
        </div>
        <aside className="space-y-5">
          <div className="rounded-3xl p-6 space-y-3 text-sm" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
            <SectionHead icon={Phone} color="amber" title="Contact" small/>
            <div className="space-y-2.5 pt-2">
              {s.website && <div className="flex items-center gap-2 text-sky-700"><Globe className="w-4 h-4"/><a href={s.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{s.website}</a></div>}
              {s.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-sky-700"/><a href={`mailto:${s.email}`} className="hover:underline break-all">{s.email}</a></div>}
              {s.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-sky-700"/><a href={`tel:${s.phone}`} className="hover:underline">{s.phone}</a></div>}
              {(s.city || s.state) && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-sky-700"/>{[s.city, s.state].filter(Boolean).join(", ")}</div>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function EventsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/events", { params: { limit: 40 } }).then(r => setItems(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="events-page">
      <div className="overline text-sky-700 mb-2">Upcoming</div>
      <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-6">Events & Conferences</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(e => (
          <div key={e.id} className="card-soft p-6" data-testid={`event-item-${e.id}`}>
            <div className="overline text-emerald-700 text-[10px] mb-2 flex items-center gap-1"><Calendar className="w-3 h-3"/>{e.type}</div>
            <h3 className="font-editorial text-xl font-bold text-slate-900">{e.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-3">{e.description}</p>
            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between text-xs text-slate-500">
              <span>{e.location}</span><span className="font-semibold text-sky-700">{e.date}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", type: "general" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/contact", form);
    setSent(true);
  };
  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="contact-page">
      <div className="overline text-sky-700 mb-2">Get in touch</div>
      <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-8">Talk to the EduTime team</h1>
      {sent ? <div className="card-soft p-8 text-center"><h2 className="font-editorial text-2xl font-bold mb-2">Thanks!</h2><p className="text-slate-600">Our editors will reach out shortly.</p></div> : (
      <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="contact-form">
        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 bg-white">
          <option value="general">General Enquiry</option>
          <option value="advertise">Advertise</option>
          <option value="press">Submit Press Release</option>
          <option value="institution">Institution Registration</option>
          <option value="supplier">Supplier Registration</option>
        </select>
        <input required placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input required placeholder="Subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <textarea required rows={5} placeholder="Message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <button className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold hover:bg-sky-800" data-testid="contact-submit-btn">Send</button>
      </form>
      )}
    </div>
  );
}

function ClaimProfileButton({ slug, name }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-full bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold flex items-center gap-1"
        data-testid="claim-open-btn"
      >
        <ShieldCheck className="w-4 h-4"/>Claim this profile
      </button>
      {open && <ClaimProfileModal slug={slug} name={name} onClose={() => setOpen(false)}/>}
    </>
  );
}

function ClaimProfileModal({ slug, name, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post(`/institutions/${slug}/claim`, f);
      setDone(true);
      toast.success("Claim submitted — editors will verify shortly");
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="claim-modal">
      <div className="bg-white rounded-3xl max-w-xl w-full p-8 relative max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100" data-testid="claim-close"><X className="w-4 h-4"/></button>
        {done ? (
          <div className="text-center py-6">
            <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto mb-3"/>
            <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-2">Claim submitted</h2>
            <p className="text-slate-600 text-sm">Thanks. Our editors will verify your claim for <span className="font-semibold text-sky-700">{name}</span> and reply on <span className="font-semibold text-sky-700">{f.email}</span>.</p>
            <button onClick={onClose} className="mt-6 px-5 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm">Close</button>
          </div>
        ) : (
          <>
            <div className="overline text-sky-700 mb-1">Claim profile</div>
            <h2 className="font-editorial text-2xl font-bold text-slate-900 mb-1">{name}</h2>
            <p className="text-sm text-slate-600 mb-6">Share verifiable contact details. Editors typically respond within 1–2 business days.</p>
            <form onSubmit={submit} className="space-y-3">
              <input required placeholder="Your full name *" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="claim-name"/>
              <input required type="email" placeholder="Institutional email *" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="claim-email"/>
              <input type="tel" placeholder="Phone (optional)" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="claim-phone"/>
              <input placeholder="Your role (Principal / Registrar / PR Officer…)" value={f.role} onChange={e => setF({...f, role: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="claim-role"/>
              <textarea rows={4} placeholder="Message to the editors (optional)" value={f.message} onChange={e => setF({...f, message: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="claim-message"/>
              <button disabled={busy} className="w-full py-3 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center justify-center gap-1 disabled:opacity-60" data-testid="claim-submit">
                <ShieldCheck className="w-4 h-4"/>{busy ? "Submitting…" : "Submit claim"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
